import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { AutocompleteEpisode, AutocompleteSeason, MediaType } from '@shared/types';
import MediaInfo from '@app/components/MediaInfo';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useAtomValue } from 'jotai';
import { wordsDataAtom, wordsLoadingAtom } from '@app/atoms/wordsAtoms';
import Spinner from 'react-bootstrap/Spinner';
import { selectedEpisodeAtom, selectedMediaAtom, selectedSeasonAtom } from '@app/atoms/selectedMediaAtoms';
import { loadingShowIdsAtom } from '@app/atoms/uiAtoms';
import { collectShowIdentifiers } from '@shared/utils/show';
import './MediaSelector.scss';

interface EpisodeSelectorProps {
  onExit?: () => void;
  onSubmit?: ({ season, episode }: { season: AutocompleteSeason | null, episode: AutocompleteEpisode | null }) => Promise<void>;
}

const MediaSelector = ({ onExit, onSubmit }: EpisodeSelectorProps) => {
  const [visible, setVisible] = useState(true);
  const [getWordsDisabled, setGetWordsDisabled] = useState<boolean>(true);

  const selectedMedia = useAtomValue(selectedMediaAtom);
  const selectedSeason = useAtomValue(selectedSeasonAtom);
  const selectedEpisode = useAtomValue(selectedEpisodeAtom);
  const wordsLoading = useAtomValue(wordsLoadingAtom);
  const wordsData = useAtomValue(wordsDataAtom);
  const loadingShowIds = useAtomValue(loadingShowIdsAtom);

  const [tmpSelectedSeason, setTmpSelectedSeason] = useState<AutocompleteSeason | null>(selectedSeason);
  const [tmpSelectedEpisode, setTmpSelectedEpisode] = useState<AutocompleteEpisode | null>(selectedEpisode);
  const selectedMediaType = selectedMedia?.type;
  const selectedShowIds = collectShowIdentifiers(selectedMedia);
  const blockedByAnotherLoading = loadingShowIds.size > 0
    && (!selectedShowIds.length || !selectedShowIds.some((id) => loadingShowIds.has(id)));
  const isSameSelectionAsLoaded = selectedMediaType === MediaType.MOVIE
    ? true
    : (tmpSelectedSeason?.num === selectedSeason?.num && tmpSelectedEpisode?.num === selectedEpisode?.num);
  const hasLoadedWordsForCurrentSelection = wordsData.words.length > 0 && isSameSelectionAsLoaded;

  const handleExit = () => {
    if (typeof onExit === 'function') {
      onExit();
    }
  };

  const handleHide = () => {
    setVisible(false);
  };

  const handleSeasonChange = (season: AutocompleteSeason) => {
    setTmpSelectedSeason(season);
    setTmpSelectedEpisode(null);
  };

  const handleSubmit = async () => {
    if (getWordsDisabled || !selectedMedia) {
      return;
    }

    // Close selector immediately so the main loading panel is visible.
    setVisible(false);
    handleExit();

    if (typeof onSubmit === 'function') {
      await onSubmit({ season: tmpSelectedSeason, episode: tmpSelectedEpisode });
    }
  };

  useEffect(() => {
    if (
      !selectedMedia
      || (selectedMedia.type === MediaType.TVSHOW && (tmpSelectedSeason === null || tmpSelectedEpisode === null))
      || wordsLoading
      || blockedByAnotherLoading
      || hasLoadedWordsForCurrentSelection
    ) {
      setGetWordsDisabled(true);
    } else {
      setGetWordsDisabled(false);
    }
  }, [wordsLoading, selectedMedia, tmpSelectedSeason, tmpSelectedEpisode, blockedByAnotherLoading, hasLoadedWordsForCurrentSelection]);

  return (
    <Modal show={visible} backdrop="static" keyboard={false} onExit={handleExit} className="media-selector">
      <Modal.Header closeButton={!wordsLoading} style={{ alignItems: 'flex-start'}} onHide={handleHide}>
        {selectedMedia && (<MediaInfo media={selectedMedia} season={tmpSelectedSeason} episode={tmpSelectedEpisode} />)}
      </Modal.Header>
      <Modal.Body>
        {selectedMedia?.seasons?.length && (<Row className="g-1 mb-1">
          {(selectedMedia?.seasons || []).map((season, i) => {
            const id = `S_${season.num}_${i}`;
            return (<Col key={id} xs="4">
              <Form.Check.Input
                name="season"
                className="btn-check"
                type="radio"
                id={id}
                checked={tmpSelectedSeason?.num === season.num}
                onChange={() => handleSeasonChange({ ...season })}
                disabled={wordsLoading}
              />
              <Form.Check.Label htmlFor={id} className="btn btn-sm btn-outline-danger w-100">{`Season ${season.num}`}</Form.Check.Label>
            </Col>)})}
          {(new Array((3 - selectedMedia.seasons.length % 3) % 3).fill(true)).map((_, i) => (<Col key={i} xs="4">
            <Button variant="outline-secondary" size="sm" className="w-100" disabled>&nbsp;</Button>
          </Col>))}
        </Row>)}
        {tmpSelectedSeason?.episodes?.length && (<Row className="g-1 mt-1">
          {(tmpSelectedSeason?.episodes || []).map((episode, i) => {
            const id = `E_${tmpSelectedSeason.num}_${episode.num}_${i}`;
            return (<Col key={id} xs="2">
              <Form.Check.Input
                name="episode"
                className="btn-check"
                type="radio"
                id={id}
                checked={tmpSelectedEpisode?.num === episode.num}
                onChange={() => setTmpSelectedEpisode({ ...episode })}
                disabled={wordsLoading}
              />
              <Form.Check.Label htmlFor={id} className="btn btn-sm btn-outline-primary w-100">{`Ep.${episode.num}`}</Form.Check.Label>
            </Col>)})}
          {(new Array((6 - tmpSelectedSeason.episodes.length % 6) % 6).fill(true)).map((_, i) => (<Col key={i} xs="2">
            <Button variant="outline-secondary" size="sm" className="w-100" disabled>&nbsp;</Button>
          </Col>))}
        </Row>)}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="w-100 text-center"
          variant="primary"
          disabled={getWordsDisabled}
          onClick={handleSubmit}
        >
          <span>Get Important Words</span>
          {wordsLoading && <span className="words-loading"><Spinner animation="border" size="sm" as="span"/></span>}
        </Button>
      </Modal.Footer>
    </Modal>);
};

export default MediaSelector;
